import styled, {
  css,
  type FlattenSimpleInterpolation,
} from 'styled-components';

import { Card as BaseCard } from '@xing-com/card';
import { lineClamp } from '@xing-com/crate-utils-line-clamp';
import { Image as BaseImage } from '@xing-com/image';
import { mediaConfined, mediaWide } from '@xing-com/layout-tokens';
import { Link as BaseLink } from '@xing-com/link';
import { Tag as BaseTag } from '@xing-com/tag';
import {
  cornerRadius3XL,
  cornerRadius4XL,
  cornerRadiusXXL,
  space3XL,
  spaceL,
  spaceS,
  spaceXL,
  spaceXXL,
  xdlColorTextOnLight,
} from '@xing-com/tokens';
import {
  BodyCopy as BaseBodyCopy,
  Headline as BaseHeadline,
} from '@xing-com/typography';

import type { ArticleImage } from './content-card';

export type Variant = keyof typeof cardVariants;

const captionVariants = {
  hero: css`
    @media ${mediaConfined} {
      bottom: ${spaceXL};
    }

    @media ${mediaWide} {
      bottom: ${space3XL};
    }
  `,
};

const Caption = styled(BaseBodyCopy)<{
  $variant?: keyof typeof captionVariants;
}>`
  position: absolute;
  color: ${xdlColorTextOnLight};
  bottom: ${spaceXL};

  ${({ $variant }) => $variant && captionVariants[$variant]};
`;

const cardVariants = {
  default: css`
    min-width: 284px;
    max-width: 349px;

    @media ${mediaConfined} {
      min-width: 350px;
      max-width: 559px;
    }
  `,
  hero: css`
    min-width: 350px;
    max-width: 559px;

    @media ${mediaConfined} {
      min-width: 560px;
      max-width: 649px;
      padding: ${spaceXXL};
      border-radius: ${cornerRadius3XL};
      gap: ${spaceXXL};
    }

    @media ${mediaWide} {
      min-width: 650px;
      max-width: 752px;
      padding: ${space3XL};
      border-radius: ${cornerRadius4XL};
      gap: ${space3XL};
    }
  `,
};

const Card = styled(BaseCard)<{ $variant: Variant; $bgColor: string }>`
  background-color: ${({ $bgColor }) => $bgColor};
  border: 0;
  border-radius: ${cornerRadiusXXL};
  padding: ${spaceXL};
  ${({ $variant }) => $variant && cardVariants[$variant]};
`;

const containerVariants = {
  default: {},
  hero: css`
    @media ${mediaConfined} {
      flex-direction: row;
    }
  `,
};

const Container = styled.div<{ $variant?: Variant }>`
  display: flex;
  flex-direction: column;
  position: relative;
  justify-content: space-between;
  gap: 0;

  @media ${mediaConfined} {
    gap: ${spaceXXL};
  }

  ${({ $variant }) => $variant && containerVariants[$variant]};
`;

const Description = styled(BaseBodyCopy)`
  margin-bottom: ${spaceXXL};
  color: ${xdlColorTextOnLight};
  ${lineClamp(2)}
`;

const Headline = styled(BaseHeadline)`
  margin: ${spaceXL} 0 ${spaceL};
  color: ${xdlColorTextOnLight};
  ${lineClamp(3)};
`;

const imageVariants = ({
  srcSquare,
  srcSquare2x,
}: ArticleImage): {
  default: FlattenSimpleInterpolation;
  hero: FlattenSimpleInterpolation;
} => ({
  default: css`
    width: 128px;
    height: 160px;

    content: url(${srcSquare});

    @media ${mediaConfined} {
      width: 144px;
      height: 180px;
    }
  `,
  hero: css`
    width: 144px;
    height: 180px;

    content: url(${srcSquare});

    @media ${mediaConfined} {
      width: 184px;
      height: 230px;

      order: 1;
      margin-right: 0;

      content: url(${srcSquare2x});
    }

    @media ${mediaWide} {
      width: 224px;
      height: 280px;
    }
  `,
});

const Image = styled(BaseImage)<{ $variant?: Variant; $image?: ArticleImage }>`
  width: 128px;
  height: 160px;

  margin-right: ${spaceS};
  height: revert-layer;
  object-fit: cover;

  ${({ $variant, $image }) =>
    $variant && $image && imageVariants($image)[$variant]};
`;

const Link = styled(BaseLink)`
  color: inherit;
  &:hover {
    text-decoration: none;
  }
`;

const tagVariants = {
  hero: css`
    @media ${mediaConfined} {
      position: relative;
    }
  `,
};

const Tag = styled(BaseTag)<{ $variant: keyof typeof tagVariants }>`
  width: fit-content;
  position: absolute;
  top: 0;
  right: 0;

  ${({ $variant }) => $variant && tagVariants[$variant]};
`;

export const Styled = {
  Caption,
  Card,
  Container,
  Description,
  Headline,
  Image,
  Link,
  Tag,
};
